import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { FaTwitter, FaEnvelope, FaLinkedin, FaGithub, FaReact, FaNode, FaPython, FaHtml5, FaGitAlt, FaDatabase } from 'react-icons/fa';
import { SiTypescript, SiTailwindcss, SiJavascript, SiMysql, SiNumpy, SiPandas, SiPytorch, SiAnaconda } from 'react-icons/si';
import { IoIosArrowUp } from 'react-icons/io';
import { useState, useEffect } from 'react';
import head1 from '../assets/head1.png';
import head2 from '../assets/head2.png';
import uowLogo from '../assets/logo-uow.png';
import sfLogo from '../assets/SF_Scholars_Program-removebg-preview.png';
import scholarshipLogo from '../assets/scholarship-removebg-preview.png';
import { Link } from 'react-router-dom';


const Container = styled.div`
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
  background-color: #FCFCFC;
`;

const HeroSection = styled.div`
  min-height: 100vh;
  display: flex;
  padding: 2rem;
  background-color: #FCFCFC;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 2rem;
  padding: 0 2rem;

  @media (max-width: 768px) {
    flex-direction: column-reverse;
    text-align: center;
  }
`;

const TextSection = styled.div`
  flex: 1;
`;

const ImageSection = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  max-width: 500px;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const Title = styled(motion.h1)`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 3.5rem;
  color: #333;
  margin-bottom: 0.5rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const Subtitle = styled(motion.p)`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.4rem;
  font-weight: 500;
  color: #666;
  margin-bottom: 2rem;
  max-width: 650px;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    margin: 0 auto;
    white-space: normal;
    max-width: 100%;
  }
`;

const ProfileImage = styled(motion.div)`
  width: 400px;
  height: 400px;
  border-radius: 50%;
  overflow: hidden;
  position: relative;
  cursor: pointer;

  @media (max-width: 1200px) {
    width: 350px;
    height: 350px;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: all 0.3s ease;
  }

  &:hover {
    img {
      transform: scale(1.1);
    }
  }
`;

const SocialLinks = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const SocialIcon = styled(motion.a)`
  color: #333;
  font-size: 1.5rem;
  transition: color 0.3s ease;
  position: relative;
  cursor: ${props => props.copyable ? 'copy' : 'pointer'};

  &:hover {
    color: #007AFF;
  }

  &::before {
    content: ${props => props.copyable ? '"Click to copy"' : 'none'};
    position: absolute;
    top: -25px;
    left: 50%;
    transform: translateX(-50%);
    background-color: #333;
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    opacity: 0;
    transition: opacity 0.2s ease;
    pointer-events: none;
    white-space: nowrap;
  }

  &:hover::before {
    opacity: ${props => props.copyable ? 1 : 0};
  }
`;

const Section = styled.section`
  padding: 3rem 2rem;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  background-color: #FCFCFC;
`;

const SectionTitle = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 1rem;
  text-align: center;
  line-height: 1.2;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const LastSectionTitle = styled(SectionTitle)`
  font-size: 2.5rem;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const EducationSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4rem;
  padding: 4rem 0;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 2rem;
  }
`;

const EducationText = styled.div`
  flex: 1;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.5rem;
  line-height: 1.6;
  color: #333;
`;

const UniversityLogo = styled.img`
  flex: 1;
  max-width: 400px;
  height: auto;
  object-fit: contain;

  @media (max-width: 768px) {
    max-width: 300px;
  }
`;

const AboutContainer = styled.div`
  display: flex;
  gap: 4rem;
  align-items: center;
  padding: 0 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const AboutText = styled.div`
  font-size: 2.5rem;
  color: #333;
  text-align: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;
  line-height: 1.2;
  width: 90%;

  p {
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 0.8rem;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
    padding: 0.5rem;
    width: 95%;
  }
`;

const HighlightSpan = styled(motion.span)`
  color: #007AFF;
  font-size: 3.75rem;
  font-weight: 700;
  display: inline-block;

  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const TagLine = styled(motion.p)`
  font-family: 'Permanent Marker', cursive;
  font-size: 2.5rem;
  color: #333;
  margin-top: 4rem;
  line-height: 1.2;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  width: 90%;
  text-decoration: underline;
  text-underline-offset: 8px;
  padding-bottom: 1rem;

  @media (max-width: 768px) {
    margin-top: 3rem;
    font-size: 2rem;
  }
`;

const AboutImage = styled.img`
  width: 400px;
  height: auto;
  border-radius: 20px;
  flex: 1;
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  padding: 0 2rem;
`;

const ProjectCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
`;

const ProjectImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
`;

const ProjectContent = styled.div`
  padding: 1.5rem;
`;

const ProjectTitle = styled.h3`
  font-size: 1.3rem;
  color: #333;
  margin-bottom: 1rem;
`;

const ProjectDescription = styled.p`
  color: #666;
  margin-bottom: 1rem;
`;

const TechStack = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const TechIcon = styled.div`
  font-size: 1.5rem;
  color: #666;
`;

const ProjectLinks = styled.div`
  display: flex;
  gap: 1rem;
`;

const ProjectLink = styled.a`
  color: #007AFF;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    text-decoration: underline;
  }
`;

const KnowMoreSection = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: #f8f9fa;
  border-radius: 20px;
  margin: 0 2rem;
`;

const KnowMoreLink = styled(Link)`
  font-size: 2.5rem;
  color: #333;
  text-decoration: none;
  display: inline-block;
  transition: color 0.3s ease;

  &:hover {
    color: #007AFF;
  }
`;

const WavingHand = styled(motion.span)`
  display: inline-block;
  margin-left: 10px;
  font-size: 2.75rem;
`;

const HighlightText = styled.span`
  color: #007AFF;
  font-weight: 600;
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 2rem;
  padding: 0;
  max-width: 1200px;
  margin: 0 auto;
`;

const SkillCard = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: white;
  border-radius: 15px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const SkillIcon = styled.div`
  font-size: 3rem;
  color: #007AFF;
  margin-bottom: 1rem;
`;

const SkillName = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1rem;
  font-weight: 500;
  color: #333;
`;

const ProjectButton = styled(Link)`
  display: block;
  width: fit-content;
  margin: 3rem auto;
  padding: 1.2rem 2.8rem;
  background-color: #FFB100;
  color: white;
  border-radius: 12px;
  text-decoration: none;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(255, 177, 0, 0.2);
  
  &:hover {
    background-color: #FFC233;
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(255, 177, 0, 0.3);
  }
  
  &:active {
    transform: translateY(1px);
    box-shadow: 0 2px 10px rgba(255, 177, 0, 0.2);
    background-color: #FF9900;
  }
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  padding: 0;
  max-width: 1200px;
  margin: -2rem auto 0;
`;

const BlogCard = styled(motion.div)`
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-5px);
  }
`;

const BlogImage = styled.div`
  width: 100%;
  height: 200px;
  background: ${props => props.placeholder ? '#f5f5f5' : 'none'};
  position: relative;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const BlogContent = styled.div`
  padding: 1.5rem;
`;

const BlogDate = styled.p`
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 0.5rem;
`;

const BlogTitle = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.2rem;
  color: #333;
  margin-bottom: 0.5rem;
  line-height: 1.4;
`;

const BlogExcerpt = styled.p`
  color: #666;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const Footer = styled.footer`
  background-color: #f8f9fa;
  padding: 2rem 0;
  margin-top: 4rem;
  border-top: 1px solid #eaeaea;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 2rem;
    text-align: center;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const CopyTooltip = styled(motion.span)`
  position: absolute;
  background: #333;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  pointer-events: none;
  white-space: nowrap;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
  z-index: 1000;
`;

const ContactLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #666;
  text-decoration: none;
  font-family: 'Space Grotesk', sans-serif;
  transition: all 0.3s ease;
  position: relative;
  cursor: ${props => props.copyable ? 'copy' : 'pointer'};

  &:hover {
    color: #007AFF;
  }

  &::before {
    content: ${props => props.copyable ? '"Click to copy"' : 'none'};
    position: absolute;
    top: -25px;
    left: 50%;
    transform: translateX(-50%);
    background-color: #333;
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    opacity: 0;
    transition: opacity 0.2s ease;
    pointer-events: none;
  }

  &:hover::before {
    opacity: ${props => props.copyable ? 1 : 0};
  }
`;

const ScrollToTop = styled(motion.button)`
  background-color: #FFB100;
  color: white;
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 177, 0, 0.2);

  &:hover {
    background-color: #FFC233;
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(255, 177, 0, 0.3);
  }

  &:active {
    transform: translateY(1px);
  }
`;

const Home = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showCopyTooltip, setShowCopyTooltip] = useState(false);
  const [tooltipText, setTooltipText] = useState('Click to copy');

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setHasScrolled(scrollPosition > 50);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const shouldShowSecondImage = isHovered || hasScrolled;

  const subtitles = [
    "Former Investment Analysis Professional",
    "Current Master by Research in Computer Science",
    "Fast and Adaptive Learner",
    "Passion for Solving Problems with Code and Data",
    "Your Next Great Teammate — in Work or Research"
  ];

  useEffect(() => {
    if (!animationComplete && currentIndex < subtitles.length) {
      const timer = setTimeout(() => {
        setCurrentIndex(prev => {
          if (prev === subtitles.length - 1) {
            setAnimationComplete(true);
            return prev;
          }
          return prev + 1;
        });
      }, currentIndex === subtitles.length - 1 ? 3000 : 2000);
      
      return () => clearTimeout(timer);
    }
  }, [currentIndex, animationComplete, subtitles.length]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleCopyEmail = async (e) => {
    e.preventDefault();
    const email = 'ychen10001@gmail.com';
    
    try {
      const textArea = document.createElement('textarea');
      textArea.value = email;
      document.body.appendChild(textArea);
      textArea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (success) {
        setTooltipText('Copied!');
        setShowCopyTooltip(true);
        
        if (navigator.vibrate) {
          navigator.vibrate(50);
        }
      } else {
        throw new Error('Copy failed');
      }
      
      setTimeout(() => {
        setShowCopyTooltip(false);
        setTimeout(() => {
          setTooltipText('Click to copy');
        }, 200);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
      setTooltipText('Copy failed');
      setShowCopyTooltip(true);
      setTimeout(() => {
        setShowCopyTooltip(false);
        setTooltipText('Click to copy');
      }, 2000);
    }
  };

  return (
    <Container>
      <HeroSection>
        <Content>
          <TextSection>
            <Title
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Hi
              <WavingHand
                animate={{ 
                  rotate: [0, 20, 0, 20, 0],
                }}
                transition={{ 
                  duration: 1.5,
                  repeat: Infinity,
                  repeatDelay: 1,
                  ease: "easeInOut"
                }}
              >
                👋🏻
              </WavingHand>
              <br />
              I'm <span style={{ color: '#007AFF' }}>Yaqi (Eva) Chen</span>
            </Title>
            
            <Subtitle
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {animationComplete ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ 
                    duration: 0.8,
                    ease: [0.175, 0.885, 0.32, 1.275]
                  }}
                  className="flex flex-col"
                  style={{ gap: '1.5rem' }}
                >
                  {subtitles.map((text, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ 
                        duration: 0.5,
                        delay: index * 0.1,
                        ease: "easeOut"
                      }}
                      style={{
                        color: index === subtitles.length - 1 ? '#007AFF' : 'inherit',
                        lineHeight: '1.4',
                        marginBottom: text.length < 40 ? '0.5rem' : '1rem',
                        fontFamily: 'Space Grotesk, sans-serif',
                        fontSize: index === subtitles.length - 1 ? '1.6rem' : '1.4rem',
                        fontWeight: index === subtitles.length - 1 ? 600 : 500
                      }}
                    >
                      {text}
                    </motion.div>
                  ))}
                </motion.div>
              ) : (
                <div 
                  className="relative flex items-center justify-start"
                  style={{
                    height: '80px',
                    marginBottom: '2rem'
                  }}
                >
                  {subtitles.map((text, index) => (
                    <motion.div
                      key={index}
                      className="absolute left-0"
                      initial={{ opacity: 0, y: 20, rotateX: -30 }}
                      animate={{ 
                        opacity: index === currentIndex ? [0, 1, 1, 0] : 0,
                        y: index === currentIndex ? [20, 0, 0, -20] : 0,
                        rotateX: index === currentIndex ? [-30, 0, 0, 30] : -30,
                        scale: index === currentIndex ? [0.9, 1, 1, 0.9] : 0.9
                      }}
                      transition={{
                        duration: index === subtitles.length - 1 ? 3 : 2,
                        times: [0, 0.1, 0.9, 1],
                        ease: [0.645, 0.045, 0.355, 1],
                      }}
                      style={{
                        color: index === subtitles.length - 1 ? '#007AFF' : 'inherit',
                        position: 'absolute',
                        width: '90%',
                        maxWidth: '500px',
                        lineHeight: '1.5',
                        transformOrigin: 'center center',
                        backfaceVisibility: 'hidden',
                        transformStyle: 'preserve-3d',
                        wordWrap: 'break-word',
                        fontFamily: 'Space Grotesk, sans-serif',
                        fontSize: index === subtitles.length - 1 ? '1.6rem' : '1.4rem',
                        fontWeight: index === subtitles.length - 1 ? 600 : 500
                      }}
                    >
                      {text}
                    </motion.div>
                  ))}
                </div>
              )}
            </Subtitle>

            <SocialLinks
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >

              <SocialIcon
                as={motion.a}
                onClick={handleCopyEmail}
                copyable={true}
                style={{ cursor: 'copy' }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaEnvelope />
                {showCopyTooltip && (
                  <CopyTooltip
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                  >
                    {tooltipText}
                  </CopyTooltip>
                )}
              </SocialIcon>
              <SocialIcon 
                href="https://www.linkedin.com/in/yaqi-c-781b8729a/" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <FaLinkedin />
              </SocialIcon>
            </SocialLinks>
          </TextSection>

          <ImageSection>
            <ProfileImage
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <img 
                src={shouldShowSecondImage ? head2 : head1} 
                alt="Profile" 
              />
            </ProfileImage>
          </ImageSection>
        </Content>
      </HeroSection>

      <Section>
        <EducationSection>
          <EducationText>
            Currently pursuing a <HighlightText>Master by research</HighlightText> degree in <HighlightText>Computer Science</HighlightText> at the University of Wollongong, with an expected graduation date of <HighlightText>July 2026</HighlightText>.
          </EducationText>
          <UniversityLogo src={uowLogo} alt="University of Wollongong Logo" />
        </EducationSection>

        <EducationSection>
          <EducationText>
            Was honored to be selected for the <span style={{ color: '#007AFF', fontWeight: 600 }}>2024 Stephen FitzGerald Scholars Program</span>, recognizing academic potential and cross-cultural engagement.
          </EducationText>
          <UniversityLogo src={sfLogo} alt="Stephen FitzGerald Scholarship Logo" />
        </EducationSection>

        <EducationSection>
          <EducationText>
            As the <span style={{ color: '#007AFF', fontWeight: 600 }}>top-ranked Chinese student</span> in 2024 by WAM, I received the <span style={{ color: '#007AFF', fontWeight: 600 }}>Antong Jiang Master of Computer Science Excellence Scholarship</span> for outstanding academic performance.
          </EducationText>
          <UniversityLogo src={scholarshipLogo} alt="Antong Jiang Scholarship Logo" />
        </EducationSection>
      </Section>

      <Section>
        <SectionTitle>I've worked on various projects with these skills.</SectionTitle>
        <SkillsGrid>
          {[
            { icon: <FaHtml5 />, name: 'HTML5' },
            { icon: <SiJavascript />, name: 'JavaScript' },
            { icon: <FaReact />, name: 'React' },
            { icon: <FaNode />, name: 'Node.js' },
            { icon: <SiMysql />, name: 'MySQL' },
            { icon: <FaPython />, name: 'Python' },
            { icon: <SiNumpy />, name: 'NumPy' },
            { icon: <SiPandas />, name: 'Pandas' },
            { icon: <SiPytorch />, name: 'PyTorch' },
            { icon: <FaDatabase />, name: 'PyG' },
            { icon: <SiAnaconda />, name: 'Anaconda' },
            { icon: <FaGitAlt />, name: 'Git' }
          ].map((skill, index) => (
            <SkillCard
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <SkillIcon>{skill.icon}</SkillIcon>
              <SkillName>{skill.name}</SkillName>
            </SkillCard>
          ))}
        </SkillsGrid>
        
        <ProjectButton 
          to="/projects"
          as={motion.a}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <motion.span
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            whileHover={{ gap: '12px' }}
          >
            View My Projects 
            <motion.span
              initial={{ x: 0 }}
              whileHover={{ x: 5 }}
            >
              →
            </motion.span>
          </motion.span>
        </ProjectButton>
      </Section>

      <Section>
        <SectionTitle> My research topic is Graph Neural Networks (GNNs), and I am gradually deepening my understanding in this area.</SectionTitle>
      </Section>

      <Section>
        <BlogGrid>
          {[
            {
              title: "Paper Reading Notes: PRODIGY",
              date: "Coming Soon",
              excerpt: "A comprehensive review of the PRODIGY paper, exploring its contributions to the field of graph neural networks.",
              image: '/PRODIGY.png'
            },
            {
              title: "Paper Reading Notes: Graph Prompt",
              date: "Coming Soon",
              excerpt: "A comprehensive review of the Graph Prompt paper, exploring its contributions to the field of graph neural networks.",
              image: '/GraphPrompt.png'
            },
            {
              title: "Spectral-based Graph Neural Networks",
              date: "Coming Soon",
              excerpt: "A review note of the Spectral-based Graph Neural Networks paper, exploring its contributions to the field of graph neural networks.",
              image: '/spactral.png'
            }
          ].map((blog, index) => (
            <BlogCard
              key={index}
              as={motion.div}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <BlogImage placeholder={!blog.image}>
                {blog.image ? (
                  <img src={blog.image} alt={blog.title} />
                ) : (
                  <motion.div
                    style={{
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(120deg, #f5f5f5 30%, #e0e0e0 38%, #f5f5f5 48%)',
                      backgroundSize: '200% 100%',
                      animation: 'shimmer 2s infinite'
                    }}
                    animate={{
                      backgroundPosition: ['100% 0', '-100% 0']
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                  />
                )}
              </BlogImage>
              <BlogContent>
                <BlogDate>{blog.date}</BlogDate>
                <BlogTitle>{blog.title}</BlogTitle>
                <BlogExcerpt>{blog.excerpt}</BlogExcerpt>
              </BlogContent>
            </BlogCard>
          ))}
        </BlogGrid>
        <ProjectButton 
          to="/blog"
          as={motion.a}
          style={{ marginTop: '3rem' }}
        >
          <motion.span
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            whileHover={{ gap: '12px' }}
          >
            View All Posts
            <motion.span
              initial={{ x: 0 }}
              whileHover={{ x: 5 }}
            >
              →
            </motion.span>
          </motion.span>
        </ProjectButton>
      </Section>

      <Section>
        <LastSectionTitle>
        I'm actively looking for internships or part-time roles where I can grow and make a difference. <br/> If something resonates, I'd love to hear from you!
          <motion.span
            style={{ display: 'inline-block', marginLeft: '8px' }}
            animate={{ 
              scale: [1, 1.2, 1],
            }}
            transition={{ 
              duration: 1.5,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut"
            }}
          >
            🫶
          </motion.span>
        </LastSectionTitle>
      </Section>

      <Footer>
        <FooterContent>
          <ContactInfo>
            <ContactLink 
              as={motion.a}
              onClick={handleCopyEmail}
              copyable={true}
              style={{ cursor: 'copy' }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaEnvelope />
              ychen10001@gmail.com
              {showCopyTooltip && (
                <CopyTooltip
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.2 }}
                >
                  {tooltipText}
                </CopyTooltip>
              )}
            </ContactLink>
            <ContactLink 
              href="https://www.linkedin.com/in/yaqi-c-781b8729a/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaLinkedin />
              LinkedIn
            </ContactLink>
          </ContactInfo>
          <ScrollToTop
            onClick={scrollToTop}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <IoIosArrowUp size={24} />
          </ScrollToTop>
        </FooterContent>
      </Footer>
    </Container>
  );
};

export default Home;
